import React, { useMemo, useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import "./RestaurantInvoicePaymentOneScreen.scss";
import knetsm from "../../Assets/images/knet.png";
import masterVisa from "../../Assets/images/masterVisa.png";
import enabill from "../../Assets/images/enabill.png";
import ApplePayButton from "../../Components/applePayButton/applePayButton";
import StandardBtn from "../../Components/standardBtn/StandardBtn";
import StateContext from "../../StateContext";
import DispatchContext from "../../DispatchContext";
import InvoiceSharedHeader from "../../Components/invoiceShared/InvoiceSharedHeader";
import InvoiceSharedFooter from "../../Components/invoiceShared/InvoiceSharedFooter";
import vendorsConfig from "../../vendorsConfig";

const RestaurantInvoicePaymentOneScreen = ({
  languageLabel = "عربي",
  onLanguageToggle,
  onProfileClick,
  onShareClick,
  onLogoutClick,
  logo,
  store,
  restaurantName = "-",
  branchName = "",
  invoiceNumber = "",
  invoiceDescription = "",
  amount = 0,
  currency = "KWD",
  customerName = "",
  customerNumber = "",
  customerEmail = "",
  cardElement = null,
  onConfirmPay,
  onApplePay,
  loading = false,
  selectedPayment = "",
  onPaymentChange = () => {},
  tapId = null,
}) => {
  const { t } = useTranslation();
  const allowedMethods = ["card", "knet"];
  const activeMethod = allowedMethods.includes(selectedPayment) ? selectedPayment : "";
  const appState = useContext(StateContext);
  const appDispatch = useContext(DispatchContext);
  const ctaClassName = useMemo(() => {
    let className = "ripos-cta";
    if (activeMethod) {
      className += " active has-icon";
      className += activeMethod === "knet" ? " knet" : " credit";
    }
    return className;
  }, [activeMethod]);
  
  const safeAmount = Number.isFinite(Number(amount)) ? Number(amount).toFixed(3) : "0.000";
  const customerTopLine = [customerName, customerNumber].filter(Boolean).join(" • ");
  const customerBottomLine = customerEmail || "-";
  const storeLogo = logo || "https://via.placeholder.com/160x160.png?text=Logo";
  const invoiceLabel = invoiceNumber ? `${t("invoice.ripos-invoice-prefix")} #${invoiceNumber}` : t("invoice.invoice");
  const [showAppleBtn, setShowAppleBtn] = useState(false);
  useEffect(() => {
    if (window.ApplePaySession && window.ApplePaySession.canMakePayments()) {
      setShowAppleBtn(true);
    }
  }, []);
  const ctaIcon = useMemo(() => {
    if (activeMethod === "knet") {
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="4.5" width="18" height="15" rx="3" stroke="currentColor" strokeWidth="1.8" />
          <path d="M6 9.5H18" stroke="currentColor" strokeWidth="1.8" />
          <path d="M7 14.5H10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M13 14.5H17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    }

    if (activeMethod === "card") {
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="2.5" y="5" width="19" height="14" rx="3" stroke="currentColor" strokeWidth="1.8" />
          <path d="M2.5 9H21.5" stroke="currentColor" strokeWidth="1.8" />
          <path d="M7 15H10.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    }

    return null;
  }, [activeMethod]);

  const selectMethod = (method) => {
    onPaymentChange(method);
  };

  return (
    <div className="ripos-page">
      <div className="ripos-shell">
        <InvoiceSharedHeader
          languageLabel={languageLabel}
          onLanguageToggle={onLanguageToggle}
          onPrimaryClick={onProfileClick}
          primaryType="icon"
          onShareClick={onShareClick}
          onLogoutClick={onLogoutClick ?? (() => appDispatch({ type: "logout" }))}
        />

        <div className="ripos-hero" style={{direction: 'ltr'}}>
          <div className="ripos-hero-top">
            <div className="ripos-restaurant">
              <div className="ripos-restaurant-logo">
                <img src={storeLogo} alt="Restaurant logo" />
              </div>
              <div>
                <h1 className="ripos-restaurant-name">{restaurantName}</h1>
                <div className="ripos-restaurant-sub">{branchName}</div>
              </div>
            </div>
            <div className="ripos-status-pill">
              <span className="ripos-status-dot" />
              {t("invoice.ripos-ready")}
            </div>
          </div>
          <div className="ripos-invoice-line">
            <div className="ripos-invoice-kicker">{t("invoice.ripos-invoice-payment-request")}</div>
            <div className="ripos-invoice-number">{invoiceLabel}</div>
          </div>
          <div className="ripos-invoice-desc">{t("invoice.This invoice covers your order at")} {restaurantName}</div>
        </div>

        <div className="ripos-amount-card">
          <div className="ripos-amount-label">{t("invoice.Amount")}</div>
          <div className="ripos-amount-value">
            {safeAmount}
            <span className="ripos-amount-currency">{currency}</span>
          </div>
        </div>

        <div className="ripos-grid">
          <div className="ripos-card ripos-customer-card">
            <div className="ripos-customer-label">{t("invoice.ripos-customer-info")}</div>
            <div className="ripos-customer-data">
              <div>{customerTopLine || "-"}</div>
              <div className="ripos-customer-email">{customerBottomLine}</div>
            </div>
          </div>

          <div className="ripos-card ripos-pay-column ripos-pay-section">
            <div className="ripos-section-title d-flex justify-content-between align-items-center">{t("invoice.Pay-With")} :</div>

          <>{(showAppleBtn && appState?.acceptedPayments.includes("apple_pay")) ? <> 
            <ApplePayButton handleClickApplePay={onApplePay} />
                      <div className="ripos-mini-divider mt-3">
                        <span>{t("login.OR")}</span>
                      </div></> : ""}</>
           

            <div className="ripos-methods-grid">
              {appState?.acceptedPayments.includes("card") ? <div
                className={`ripos-method-card ${!store?.tap ? "disabledBtn" : ""} ${activeMethod === "card" ? "active" : ""}`}
                tabIndex={0}
                role="button"
                aria-label="Pay with credit card"
                onClick={() => selectMethod("card")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") selectMethod("card");
                }}
              >
                <div className="ripos-method-icon">
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <img className="mb-1" src={masterVisa} alt="credit" style={{ display: 'block' }}  width={25} />
                </div>  
                </div>
                <div className="ripos-method-name">{t("pay.creadit").toUpperCase()} </div>
              </div> : ""}

              {appState?.acceptedPayments.includes("knet") ? <div
                className={`ripos-method-card ${activeMethod === "knet" ? "active" : ""}`}
                tabIndex={0}
                role="button"
                aria-label="Pay with KNET"
                onClick={() => selectMethod("knet")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") selectMethod("knet");
                }}
              >
                <div className="ripos-method-icon">
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <img className="mb-1" src={knetsm} alt="knet" style={{ display: 'block' }}  width={25} />
                </div> 
                </div>
                <div className="ripos-method-name">{t("pay.knet").toUpperCase()}</div>
              </div> : ""}
            </div>

            {activeMethod === "card" && cardElement ? (
              <div className="ripos-gosell-block">
                <div className="ripos-gosell-element">{cardElement}</div>
              </div>
            ) : null}

            { 
            <StandardBtn
              className={ctaClassName}
              onClick={onConfirmPay}
              disabled={ !activeMethod || loading}
              loading={loading}
              icon={ctaIcon}
              loadingText={t("invoice.ripos-processing")}
              defaultText={t("invoice.Confirm-&-Pay").toUpperCase()}
            />}
          </div>
        </div>

        <InvoiceSharedFooter
          text={t("invoice.ripos-secure-payment")}
          brandImage={enabill}
          brandAlt="enabill"
        />
      </div>
    </div>
  );
};

export default RestaurantInvoicePaymentOneScreen;
